import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  calculateCartWithOffers,
  calculateOrderCartV2,
} from "../../../utils/APICalls/APICalls";
import { buildAppliedOffers } from "../../../utils/cart/appliedOffer";
import {
  enrichOrderLineAddonsFromLookups,
  responseLinesToOrderLines,
} from "../../../utils/cart/orderLineDisplay";
import {
  ensureOrderLineIds,
  stripAddonCustomTaxPercentages,
  stripAddonDisplayNames,
} from "../../../utils/cart/orderLineMappers";
import { sanitizeCartActionsForCalculate } from "../../../utils/cart/sanitizeCartActions";
import { OrderTypesEnum, ROUNDING_ENUM } from "../../../utils/enums";
import envConfig from "../../../utils/env.config";
import { getOrderTypeKeyStringByValue } from "../../../utils/help/orderType/orderType";
import { AppDispatch, RootState } from "../app/store";
import {
  selectLoyaltyCartLines,
  selectLoyaltyRedemption,
} from "../selectors/loyaltyCartSelectors";
import { selectUseLoyaltyService } from "../selectors/loyaltyServiceSelectors";
import { selectMenuLookups } from "../selectors/menuLookupSelectors";
import { selectPayWithPointsRequest } from "../selectors/payWithPointsSelectors";
import { shiftOffersArray } from "../slices/cartAndLoyaltySlice";
import { incrementLatestCartVersion } from "../slices/cartLoyaltyV2Slice";
import { CartInput, CartOutput } from "../types/CartLoyaltyV2Types";
import { OrderCartV2Input, OrderCartV2Output } from "../types/OrderCartV2Types";
import {
  selectCurrentDeliveryConfig,
  selectDeliveryFees,
} from "../selectors/deliverySelectors";

export const calculateCartWithOffersCall = createAsyncThunk(
  "calculateCartWithOffersCall",
  // eslint-disable-next-line
  async (params: { retryCount?: number }, { getState, dispatch }) => {
    const state = getState() as RootState;
    const franchiseId =
      state.cartAndLoyaltyReducer.cart.franchiseId > 0
        ? state.cartAndLoyaltyReducer.cart.franchiseId
        : state.config.selectedFranchise?.id;

    const cartInput: CartInput = {
      restaurantId:
        state.config.restaurantInfo?.id || parseInt(envConfig.restaurantId!),
      franchiseId: franchiseId ?? state.cartAndLoyaltyReducer.cart.franchiseId,
      actions: sanitizeCartActionsForCalculate(
        state.cartLoyaltyV2Reducer.cartActions,
        franchiseId
      ),
      hasRoundingMethodAndLevel:
        state.cartAndLoyaltyReducer.hasRoundingMethodAndLevel,
      servicePercentage: state.config.restaurantInfo?.service || 0,
      vatPercentage: state.config.restaurantInfo?.vat || 0,
      deliveryFees: selectDeliveryFees(state) || 0,
      orderType:
        getOrderTypeKeyStringByValue(state.queryParams.orderTypeParam) ||
        getOrderTypeKeyStringByValue(OrderTypesEnum.TAKE_OUT),
      pointsPerOneCredit:
        state.cartAndLoyaltyReducer.pointingSystem?.pointsPerOneCredit,
      currencyRoundingLevel:
        state.config.restaurantInfo?.currencyRoundingLevel || undefined,
      currencyRoundingMethod:
        state.config.restaurantInfo?.currencyRoundingMethod || undefined,
      deliveryServicePercentageFees:
        selectCurrentDeliveryConfig(state)?.extraConfig
          ?.deliveryServicePercentageFees,
      applyDiscountAfterVat:
        state.config.restaurantInfo?.config?.applyDiscountAfterVat,
      calculateVatExcludingServiceFee:
        state.config.restaurantConfig?.calculateVatExcludingServiceFee,
      isTaxInclusive: state.config.selectedFranchise?.isTaxInclusive,
    };
    const cart: { data: CartOutput } = await calculateCartWithOffers(cartInput);

    dispatch(shiftOffersArray(cart.data.redeemedOffer));

    return cart.data;
  }
);

// Module-level AbortController — cancelled on every fresh (non-retry) dispatch
let activeV2Controller: AbortController | null = null;

export const calculateOrderCartV2Call = createAsyncThunk(
  "calculateOrderCartV2Call",
  async (
    params: { retryCount?: number; cartVersion: number },
    { getState }
  ) => {
    const state = getState() as RootState;

    // Cancel any existing in-flight request on fresh dispatches only
    if (!params.retryCount) {
      activeV2Controller?.abort();
      activeV2Controller = new AbortController();
    } else if (!activeV2Controller) {
      activeV2Controller = new AbortController();
    }

    const restaurantId =
      state.config.restaurantInfo?.id || parseInt(envConfig.restaurantId!);

    const { currencyRoundingMethod, currencyRoundingLevel } =
      state.config.restaurantInfo ?? {};
    const applyDiscountAfterVat =
      state.config.restaurantInfo?.config?.applyDiscountAfterVat;
    const calculateVatExcludingServiceFee =
      state.config.restaurantConfig?.calculateVatExcludingServiceFee;

    const payWithPoints = selectPayWithPointsRequest(state);
    const cartLines = ensureOrderLineIds(selectLoyaltyCartLines(state));
    // A tax-inclusive branch's prices already carry the tax: send no tax rate,
    // neither the chain's nor any line's or addon's custom one.
    const isTaxInclusive =
      state.config.selectedFranchise?.isTaxInclusive ?? false;
    // Strip client-only `displayName` from addons (including nested ones)
    // before the calculate API call.
    const lines = cartLines.map((line) => {
      const addons = stripAddonDisplayNames(line.addons);
      const { customTaxPercentage, ...rest } = line;

      return {
        ...rest,
        ...(!isTaxInclusive &&
          customTaxPercentage !== undefined && { customTaxPercentage }),
        addons: isTaxInclusive
          ? stripAddonCustomTaxPercentages(addons)
          : addons,
      };
    });

    const toPercent = (fraction?: number) =>
      Math.round((fraction ?? 0) * 100 * 100) / 100;

    // Service charge applies only to dine-in; delivery fees only to DeliveryV2.
    const orderType = state.queryParams?.orderTypeParam;

    const mapRoundingMethodToLoyalty = (roundingMethod: ROUNDING_ENUM) => {
      switch (roundingMethod) {
        case ROUNDING_ENUM.UP:
          return "UP";
        case ROUNDING_ENUM.DOWN:
          return "DOWN";
        case ROUNDING_ENUM.AVERAGE:
          return "ROUND";
        case ROUNDING_ENUM.NONE:
          return "NONE";
      }
    };

    const body: OrderCartV2Input = {
      cartVersion: params.cartVersion,
      config: {
        fees: {
          taxPercentage: isTaxInclusive
            ? 0
            : toPercent(state.config.restaurantInfo?.vat),
          serviceChargePercentage:
            orderType === OrderTypesEnum.DINE_IN
              ? toPercent(state.config.restaurantInfo?.service)
              : 0,
          deliveryFees:
            orderType === OrderTypesEnum.DELIVERYV2
              ? selectDeliveryFees(state) ?? 0
              : 0,
        },
        pricing: {
          discountCalculationPhase: applyDiscountAfterVat
            ? "AFTER_TAX"
            : "BEFORE_TAX",
          taxCalculationBase: calculateVatExcludingServiceFee
            ? "SUBTOTAL"
            : "SUBTOTAL_PLUS_SERVICE",
          ...(currencyRoundingMethod && {
            rounding: {
              method: mapRoundingMethodToLoyalty(currencyRoundingMethod),
              precision: currencyRoundingLevel ?? 2,
            },
          }),
        },
        behavior: {
          offerApplicationMode: "SPLIT",
          mergeIdenticalLines: true,
          previewUnappliedExplicitOffers: true,
        },
        ...(payWithPoints && {
          payWithPoints: payWithPoints.useMaxPoints
            ? { useMaxPoints: true }
            : { points: payWithPoints.requestedPoints },
        }),
      },
      lines,
      appliedOffers: buildAppliedOffers(
        selectLoyaltyRedemption(state),
        cartLines
      ),
    };

    const response: { data: OrderCartV2Output } = await calculateOrderCartV2(
      body,
      restaurantId,
      activeV2Controller.signal,
      // Lets the backend skip its tax-group enrichment for a tax-inclusive
      // branch, which would otherwise re-add the rates stripped above.
      state.config.selectedFranchise?.id
    );

    activeV2Controller = null;

    const orderLines = enrichOrderLineAddonsFromLookups(
      responseLinesToOrderLines(response.data.lines, cartLines),
      selectMenuLookups(state)
    );

    return {
      loyaltyCartResult: response.data,
      orderLines,
      cartVersion: params.cartVersion,
    };
  }
);

export const triggerCartRecalculate =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (selectUseLoyaltyService(state)) {
      const cartVersion = state.cartLoyaltyV2Reducer.latestCartVersion + 1;
      dispatch(incrementLatestCartVersion());
      dispatch(calculateOrderCartV2Call({ cartVersion }));
    } else {
      dispatch(calculateCartWithOffersCall({}));
    }
  };
